import { createApp, h, resolveComponent } from 'vue';
import { vMaska } from 'maska';
import { handleSplashScreen } from 'freedom-shared';
import {
  directives,
  controllerModel,
  BottomSheetPlugin,
  ModalPlugin,
} from 'shared';

import { clearPersistedPassengers } from '@/widgets/passengers-form';

import { sentryModel } from '@/shared/model';
import {
  initDeferredThirdPartyTags,
  preloadResultsLoaderAsset,
} from '@/shared/lib';

import { BloomreachPromoAlert, NpsSurveyTrigger } from './ui';
import { withProviders } from './providers';
import '@/assets/css/main.css';

const App = {
  name: 'FreedomAviaApp',
  render: () => [
    h(resolveComponent('router-view')),
    h(BloomreachPromoAlert),
    h(NpsSurveyTrigger),
  ],
};

const app = createApp(App);

withProviders(app)
  .then(() => {
    app
      .use(ModalPlugin, {
        controllerInjectionKey:
          controllerModel.injectionSymbols.MODAL_CONTROLLER_SYMBOL,
      })
      .use(BottomSheetPlugin, {
        controllerInjectionKey:
          controllerModel.injectionSymbols.BOTTOM_SHEET_CONTROLLER_SYMBOL,
      })
      .directive('maska', vMaska)
      .directive('price', directives.vPrice)
      .mount('#app');

    preloadResultsLoaderAsset();

    handleSplashScreen();

    // Clear the persisted passenger draft whenever the user leaves the app, so a
    // later visit starts from the backend source of truth instead of restoring
    // data that may have gone stale meanwhile (e.g. a DOB/citizenship changed in
    // the cabinet). `visibilitychange -> hidden` covers backgrounding to another
    // mini-app (the webview is kept alive, so sessionStorage would otherwise
    // survive); `pagehide` covers a hard unload. In-app SPA navigation does not
    // fire either, and the active booking lives in memory, so this never wipes a
    // draft mid-flow — it only takes effect on the next fresh entry.
    window.addEventListener('pagehide', clearPersistedPassengers);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        clearPersistedPassengers();
      }
    });

    // Load GTM and the marketing pixels it cascades to only after the app is
    // interactive, so they never compete with first paint.
    initDeferredThirdPartyTags();
  })
  .catch((err: unknown) => {
    // If any provider throws (e.g. createI18n on a corrupted locale chunk),
    // neither mount() nor handleSplashScreen() runs — the user sees a white
    // screen forever.  Tear down the splash screen and log the failure so
    // the app degrades to a blank page instead of an infinite spinner.
    console.error('[app] withProviders failed:', err);
    try {
      sentryModel.logBaseError({
        error: err instanceof Error ? err : new Error(String(err)),
        description: '[app] withProviders failed',
        level: sentryModel.level.FATAL,
      });
    } catch {
      // Sentry may not be initialised yet; ignore.
    }
    handleSplashScreen();
  });
